import { useCallback, useState } from "react";
import {
  confirmAuthenticator,
  disableAuthenticator,
  enrollAuthenticator,
} from "../../../services/mfa/mfaSettingsApi";
import { describeMfaError } from "../../../services/mfa/mfaErrorMessages";

// `isActive`: a first setup (not ACTIVE) needs no step-up (docs/specs/authenticator-mfa-ssn-fallback.md, Flow B).
// Reconfigure and disable always step up; so does a setup the server refuses with STEP_UP_REQUIRED.
const useSecurityFlows = ({ stepUp, refresh, isActive = true }) => {
  const [confirm, setConfirm] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [enrollBusy, setEnrollBusy] = useState(false);
  const [enrollError, setEnrollError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [signInRequired, setSignInRequired] = useState(false);
  const [mobileStep, setMobileStep] = useState(false);

  const handleError = useCallback(async (error) => {
    const detail = describeMfaError(error);
    setSignInRequired(detail.signIn);
    if (detail.refresh) {
      setNotice(detail.message);
      await refresh();
      return;
    }
    setNotice(detail.message);
  }, [refresh]);

  const runAction = useCallback(async (kind, retried = false) => {
    const stepless = kind === "setup" && !isActive && !retried;
    const stepUpToken = stepless ? undefined : await stepUp.begin();
    if (!stepless && !stepUpToken) return;

    try {
      if (kind === "disable") {
        await disableAuthenticator({ stepUpToken });
        await refresh();
        return;
      }
      const response = await enrollAuthenticator({ stepUpToken });
      setEnrollError(null);
      setEnrollment(response);
    } catch (error) {
      const detail = describeMfaError(error);
      if (detail.reopenStepUp && !retried) {
        await runAction(kind, true);
        return;
      }
      await handleError(error);
    }
  }, [handleError, refresh, stepUp, isActive]);

  const setup = useCallback(() => runAction("setup"), [runAction]);
  const ask = useCallback((kind) => setConfirm({ kind }), []);
  const cancelConfirm = useCallback(() => setConfirm(null), []);
  const runConfirmed = useCallback(() => {
    const kind = confirm?.kind;
    setConfirm(null);
    if (kind) runAction(kind);
  }, [confirm, runAction]);

  const confirmEnrollment = useCallback(async (code) => {
    if (!enrollment?.enrollment_id) return;
    setEnrollBusy(true);
    setEnrollError(null);
    try {
      const response = await confirmAuthenticator({ enrollmentId: enrollment.enrollment_id, code });
      setEnrollment(null);
      // No codes screen since docs/specs/mfa-ux-cleanup.md §1. With no mobile on file the "add a mobile" step comes
      // before the refresh (docs/specs/authenticator-mfa-sms-enroll.md).
      if (response?.sms_alerts === false) setMobileStep(true);
      else await refresh();
    } catch (error) {
      const detail = describeMfaError(error);
      setSignInRequired(detail.signIn);
      if (detail.code === "ENROLLMENT_EXPIRED") {
        setEnrollment(null);
        setNotice(detail.message);
      } else if (detail.refresh) {
        setNotice(detail.message);
        await refresh();
      } else {
        setEnrollError(detail.message);
      }
    } finally {
      setEnrollBusy(false);
    }
  }, [enrollment, refresh]);

  const restartEnrollment = useCallback(() => {
    setEnrollment(null);
    setEnrollError(null);
    runAction("setup");
  }, [runAction]);

  const cancelEnrollment = useCallback(() => {
    setEnrollment(null);
    setEnrollError(null);
  }, []);

  const finishMobileStep = useCallback(async () => {
    setMobileStep(false);
    await refresh();
  }, [refresh]);

  return {
    confirm, enrollment, enrollBusy, enrollError, notice, signInRequired, mobileStep,
    setup, ask, cancelConfirm, runConfirmed, confirmEnrollment, restartEnrollment,
    cancelEnrollment, finishMobileStep,
  };
};

export default useSecurityFlows;
